import clsx from 'clsx';
import Tag from 'components/Tag';
import {
  format,
  isToday,
  isTomorrow,
  startOfToday,
  isAfter,
  isBefore,
} from 'date-fns';
import { CalendarOutline } from 'react-ionicons';
import { getDateRange } from 'screens/Orders/utils';
import ScheduledItem from './ScheduledItem';
import { motion } from 'framer-motion';
import TodayButton from './TodayButton';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarEvent } from 'api/orders/schemas';
import Skeleton from 'components/Skeleton/Skeleton';
const SCROLL_ANIMATION_DURATION = 400;
interface EventListProps {
  selectedDate: Date;
  onResetToToday: () => void;
  events: Record<string, CalendarEvent[]>;
  isLoading?: boolean;
}
const EventList = ({
  selectedDate,
  onResetToToday,
  events,
  isLoading = false,
}: EventListProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const todayRef = useRef<HTMLDivElement | null>(null);
  const [isTodayVisible, setIsTodayVisible] = useState(true);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    if (!todayRef.current || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTodayVisible(entry.isIntersecting);
      },
      {
        root: containerRef.current,
        threshold: 0.01,
      },
    );
    observer.observe(todayRef.current);
    return () => observer.disconnect();
  }, [containerRef, todayRef, selectedDate]);

  const today = startOfToday();
  const showTodayButton = !isToday(selectedDate) || !isTodayVisible;

  const isFuture = useMemo(() => {
    if (isResetting) return false;
    return isAfter(selectedDate, today)
      ? true
      : isToday(selectedDate) && !isTodayVisible;
  }, [isTodayVisible, selectedDate, today, isResetting]);

  const handleResetToToday = () => {
    setIsResetting(true);
    onResetToToday();
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    setTimeout(() => {
      setIsResetting(false);
    }, SCROLL_ANIMATION_DURATION);
  };
  const startDate = useMemo(
    () => selectedDate ?? startOfToday(),
    [selectedDate],
  );
  const dateRange = useMemo(() => getDateRange(startDate), [startDate]);

  if (isLoading) return <Skeleton className="h-full w-full !rounded" />;

  return (
    <div className="relative flex h-0 min-h-full flex-col gap-6">
      {showTodayButton && (
        <TodayButton onClick={handleResetToToday} isFuture={isFuture} />
      )}
      <div
        ref={containerRef}
        className="vertical-scrollbar flex h-full flex-col gap-8 overflow-y-auto will-change-transform scrollbar-none"
      >
        {dateRange.map((date) => {
          const dateKey = format(date, 'yyyy-MM-dd');
          const eventList = events[dateKey] ?? [];
          const isTodayDate = isToday(date);
          const isTomorrowDate = isTomorrow(date);
          const isPastDate = isBefore(date, startOfToday());
          return (
            <motion.div
              layout
              transition={{ duration: 0 }}
              key={dateKey}
              className={clsx('flex flex-col gap-2 self-stretch')}
              ref={isTodayDate ? todayRef : null}
            >
              <div className="relative flex gap-2">
                <Tag
                  content={{
                    type: 'label+icon',
                    label: `${
                      isTodayDate
                        ? 'Today - '
                        : isTomorrowDate
                          ? 'Tomorrow - '
                          : ''
                    } ${format(date, 'EEE, MMM d')}`,
                    icon: (
                      <CalendarOutline
                        color={isTodayDate ? '#9796F3' : '#94A3B8'}
                        width="14px"
                        height="14px"
                        cssClasses="max-w-full"
                      />
                    ),
                  }}
                  status="custom"
                  className={clsx(
                    'flex-row-reverse gap-1 bg-[#94A3B814] !bg-opacity-[8%] text-[#94A3B8]',
                    {
                      '!bg-[#8989FF] !text-[#8989FF]': isTodayDate,
                    },
                    '[&>span:first-child]:mr-0',
                  )}
                />
              </div>
              <div
                className={clsx(
                  'flex flex-col items-start gap-2 self-stretch',
                  isPastDate && 'opacity-50',
                )}
              >
                {eventList.length === 0 ? (
                  <p className="px-2 py-1 text-xs font-semibold text-content-base-subdued">
                    No scheduled events
                  </p>
                ) : (
                  eventList.map((event) => (
                    <ScheduledItem key={event.time} event={event} />
                  ))
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
export default EventList;
